// PassageCeintureGrid.jsx (version tableau optimisée)

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import useScrollToTop from "../../../hooks/useScollToTop";

import AdherentPassageCeinture from "./AdherentPassageCeinture";
import Loader from "./../../../utils/Loader.jsx";

import { getGroupsList } from "./../../../services/groupServices";
import { getAllAdherentsListWithGroup } from "./../../../services/adherentServices";

import {
  getBeltsList,
  getLatestBeltHistoriesByAdherents,
  addBeltHistoryBulk,
} from "./../../../services/beltServices";

import { getSettings } from "./../../../services/settingsServices";

import { FaInfoCircle, FaTimes } from "react-icons/fa";
import { GiBlackBelt } from "react-icons/gi";

import BeltCounts from "../../Home/BeltCounts";
import ScrollIndicator from "./../../Home/ScrollIndicator";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

function unwrapSettings(settingsData) {
  if (Array.isArray(settingsData)) return settingsData[0] || {};
  return settingsData || {};
}

function getTodayInputDate() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function getPreviousSeason(currentSeason) {
  const [startYear, endYear] = String(currentSeason || "")
    .split("-")
    .map(Number);

  if (!startYear || !endYear) return "";

  return `${startYear - 1}-${endYear - 1}`;
}

function getNextSeason(currentSeason) {
  const [startYear, endYear] = String(currentSeason || "")
    .split("-")
    .map(Number);

  if (!startYear || !endYear) return "";

  return `${startYear + 1}-${endYear + 1}`;
}

function buildSeasonOptions(activeSeason) {
  if (!activeSeason) return [];

  return [getPreviousSeason(activeSeason), activeSeason, getNextSeason(activeSeason)]
    .filter(Boolean)
    .filter((value, index, array) => array.indexOf(value) === index);
}

function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function formatDateFr(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR");
}

function toAbsolute(url) {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (!value.startsWith("/uploads")) {
    return value;
  }

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

  return `${base}${value}`;
}

function getAdherentName(adherent = {}) {
  return `${adherent?.nom || ""} ${adherent?.prenom || ""}`.trim();
}

const PassageCeintureGrid = () => {
  useScrollToTop();

  const initialSeasonAppliedRef = useRef(false);

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [groups, setGroups] = useState([]);
  const [adherents, setAdherents] = useState([]);
  const [belts, setBelts] = useState([]);
  const [beltHistories, setBeltHistories] = useState({});

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSeason, setSelectedSeason] = useState("");
  const [seasons, setSeasons] = useState([]);
  const [filterGroup, setFilterGroup] = useState("");
  const [filterBelt, setFilterBelt] = useState("");

  const [loading, setLoading] = useState(true);
  const [bulkSaving, setBulkSaving] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBeltId, setBulkBeltId] = useState("");
  const [bulkDate, setBulkDate] = useState(getTodayInputDate());

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAdherent, setSelectedAdherent] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const [settingsData, groupList, adherentsList, beltsData] =
        await Promise.all([
          getSettings(),
          getGroupsList(),
          getAllAdherentsListWithGroup(),
          getBeltsList(),
        ]);

      const settings = unwrapSettings(settingsData);
      const activeSeason = settings?.saison_active || "";

      const normalizedGroups = Array.isArray(groupList) ? groupList : [];
      const normalizedAdherents = Array.isArray(adherentsList)
        ? adherentsList
        : [];
      const normalizedBelts = Array.isArray(beltsData) ? beltsData : [];

      setGroups(normalizedGroups);
      setAdherents(normalizedAdherents);
      setBelts(normalizedBelts);
      setSeasons(buildSeasonOptions(activeSeason));

      if (!initialSeasonAppliedRef.current) {
        initialSeasonAppliedRef.current = true;
        setSelectedSeason(activeSeason || "");
      }

      const adherentIds = normalizedAdherents
        .map((adherent) => adherent?.id)
        .filter(Boolean);

      if (!adherentIds.length) {
        setBeltHistories({});
        return;
      }

      const historiesMap = await getLatestBeltHistoriesByAdherents(adherentIds);

      setBeltHistories(historiesMap || {});
    } catch (error) {
      console.error("Erreur chargement passages de ceinture :", error);
      toast.error("Erreur lors du chargement des données.");

      setGroups([]);
      setAdherents([]);
      setBelts([]);
      setBeltHistories({});
      setSeasons([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const groupOptions = useMemo(() => {
    return Array.from(
      new Set(groups.map((group) => group?.nom).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b, "fr"));
  }, [groups]);

  const sortedBelts = useMemo(() => {
    return [...belts].sort((a, b) => {
      const ordreA = Number(a?.ordre ?? a?.id ?? 0);
      const ordreB = Number(b?.ordre ?? b?.id ?? 0);

      return ordreA - ordreB;
    });
  }, [belts]);

  const filtered = useMemo(() => {
    const query = normalizeText(searchTerm);

    return adherents.filter((adherent) => {
      const fullName = normalizeText(
        `${adherent?.nom || ""} ${adherent?.prenom || ""}`,
      );

      const groupNames = Array.isArray(adherent?.Groups)
        ? adherent.Groups.map((group) => group?.nom || "")
        : [];

      const currentBelt = beltHistories?.[adherent.id]?.BeltColor?.nom || "";

      const inSeason =
        !selectedSeason || adherent?.saison_active === selectedSeason;

      const inSearch = !query || fullName.includes(query);

      const inGroup =
        !filterGroup || groupNames.some((groupName) => groupName === filterGroup);

      const inBelt = !filterBelt || currentBelt === filterBelt;

      return inSeason && inSearch && inGroup && inBelt;
    });
  }, [
    adherents,
    searchTerm,
    selectedSeason,
    filterGroup,
    filterBelt,
    beltHistories,
  ]);

  useEffect(() => {
    const visibleIds = new Set(filtered.map((adherent) => adherent.id));

    setSelectedIds((prev) => {
      const next = new Set();

      prev.forEach((id) => {
        if (visibleIds.has(id)) {
          next.add(id);
        }
      });

      return next;
    });
  }, [filtered]);

  const allChecked =
    filtered.length > 0 && filtered.every((adherent) => selectedIds.has(adherent.id));

  const toggleAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(filtered.map((adherent) => adherent.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const toggleOne = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }

      return next;
    });
  };

  const clearBulkSelection = () => {
    setSelectedIds(new Set());
    setBulkBeltId("");
    setBulkDate(getTodayInputDate());
  };

  const openInfo = (adherentId) => {
    setSelectedAdherent(adherentId);
    setIsModalOpen(true);
  };

  const closeInfo = () => {
    setIsModalOpen(false);
    setSelectedAdherent(null);
  };

  const handleHistoryChanged = async () => {
    await fetchData();
  };

  const applyBulk = async () => {
    if (selectedIds.size === 0) {
      toast.warn("Sélectionne au moins un adhérent.");
      return;
    }

    if (!bulkBeltId) {
      toast.warn("Choisis une ceinture.");
      return;
    }

    if (!bulkDate) {
      toast.warn("Choisis une date.");
      return;
    }

    const adherentIds = Array.from(selectedIds).map(Number);
    const beltId = Number(bulkBeltId);

    try {
      setBulkSaving(true);

      const result = await addBeltHistoryBulk({
        adherent_ids: adherentIds,
        belt_id: beltId,
        date_passage: bulkDate,
      });

      const created = Number(result?.created || 0);
      const skipped = Number(result?.skipped || 0);

      if (created > 0) {
        toast.success(
          `${created} passage${created > 1 ? "s" : ""} créé${
            created > 1 ? "s" : ""
          }${skipped ? ` • ${skipped} déjà existant(s)` : ""}.`,
        );
      } else if (skipped > 0) {
        toast.info("Tous les passages existaient déjà pour cette date.");
      } else {
        toast.warn("Aucun passage n’a été créé.");
      }

      clearBulkSelection();
      await fetchData();
    } catch (error) {
      console.error("Erreur passage groupé :", error);
      toast.error("Erreur lors de l'application du passage de grade.");
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <section className="w-full">
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        <HeaderTitle
          role="Enseignant"
          roleId="3"
          title="Passage de Grades"
          subtitle="Visualisez et gérez les passages de grade de vos groupes."
          icon={GiBlackBelt}
          align="left"
          className="mb-4"
        />

        <DashboardNavigation role={userRole} />
        <ScrollIndicator />

        <div className="mx-3 mt-4 overflow-hidden rounded-2xl bg-white/80 shadow ring-1 ring-slate-200/70 backdrop-blur dark:bg-slate-900/60 dark:ring-slate-700/70 sm:mx-6">
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 text-white sm:px-8">
            <h3 className="text-center text-lg font-semibold sm:text-xl">
              Gestion des passages de grade
            </h3>

            <p className="mx-auto max-w-3xl text-center text-sm/6 opacity-95">
              Filtre par saison, groupe, ceinture & recherche par nom.
              Sélectionne plusieurs adhérents pour appliquer un passage groupé.
            </p>
          </div>

          <BeltCounts />

          <div className="px-5 pb-4 pt-5 sm:px-8">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <input
                type="text"
                placeholder="Rechercher par nom ou prénom…"
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />

              <select
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                value={selectedSeason}
                onChange={(event) => setSelectedSeason(event.target.value)}
              >
                <option value="">Toutes les saisons</option>

                {seasons.map((season) => (
                  <option key={season} value={season}>
                    {season}
                  </option>
                ))}
              </select>

              <select
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                value={filterGroup}
                onChange={(event) => setFilterGroup(event.target.value)}
              >
                <option value="">Tous les groupes</option>

                {groupOptions.map((groupName) => (
                  <option key={groupName} value={groupName}>
                    {groupName}
                  </option>
                ))}
              </select>

              <select
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                value={filterBelt}
                onChange={(event) => setFilterBelt(event.target.value)}
              >
                <option value="">Toutes les ceintures</option>

                {sortedBelts.map((belt) => (
                  <option key={belt.id} value={belt.nom}>
                    {belt.nom}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-700 dark:text-slate-300">
              <span className="inline-flex items-center rounded-lg bg-slate-100 px-3 py-1 dark:bg-slate-800">
                <span className="mr-2 opacity-70">Résultats</span>
                <span className="font-semibold">{filtered.length}</span>
              </span>

              <span className="inline-flex items-center rounded-lg bg-slate-100 px-3 py-1 dark:bg-slate-800">
                <span className="mr-2 opacity-70">Total adhérents</span>
                <span className="font-semibold">{adherents.length}</span>
              </span>

              {selectedIds.size > 0 ? (
                <span className="inline-flex items-center rounded-lg bg-blue-50 px-3 py-1 dark:bg-blue-900/30">
                  <span className="mr-2 opacity-70">Sélection</span>
                  <span className="font-semibold">{selectedIds.size}</span>
                </span>
              ) : null}
            </div>
          </div>

          <div className="px-5 pb-6 sm:px-8">
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white/90 dark:border-slate-800 dark:bg-slate-900/40">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-slate-50/80 text-xs uppercase tracking-wide dark:bg-slate-900/80">
                    <th className="w-[44px] px-3 py-3 text-left font-semibold text-slate-700 dark:text-white">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={allChecked}
                        onChange={(event) => toggleAll(event.target.checked)}
                        aria-label="Tout sélectionner"
                      />
                    </th>

                    <th className="px-3 py-3 text-left font-semibold text-slate-700 dark:text-white">
                      Adhérent
                    </th>

                    <th className="px-3 py-3 text-left font-semibold text-slate-700 dark:text-white">
                      Nom Prénom
                    </th>

                    <th className="px-3 py-3 text-left font-semibold text-slate-700 dark:text-white">
                      Date de naissance
                    </th>

                    <th className="px-3 py-3 text-left font-semibold text-slate-700 dark:text-white">
                      Ceinture actuelle
                    </th>

                    <th className="px-3 py-3 text-left font-semibold text-slate-700 dark:text-white">
                      Dernier passage
                    </th>

                    <th className="px-3 py-3 text-left font-semibold text-slate-700 dark:text-white">
                      Info
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
                  {filtered.map((adherent) => {
                    const history = beltHistories?.[adherent.id] || null;
                    const beltName = history?.BeltColor?.nom || "—";
                    const lastDate = formatDateFr(history?.date_passage);

                    const photo =
                      toAbsolute(adherent.photo_url) ||
                      toAbsolute(adherent.photo) ||
                      "/img/user_avatar.jpg";

                    return (
                      <tr
                        key={adherent.id}
                        className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                      >
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={selectedIds.has(adherent.id)}
                            onChange={(event) =>
                              toggleOne(adherent.id, event.target.checked)
                            }
                            aria-label={`Sélectionner ${getAdherentName(
                              adherent,
                            )}`}
                          />
                        </td>

                        <td className="px-3 py-3">
                          <img
                            src={photo}
                            alt={getAdherentName(adherent)}
                            className="h-10 w-10 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                            onError={(event) => {
                              event.currentTarget.src = "/img/user_avatar.jpg";
                            }}
                          />
                        </td>

                        <td className="whitespace-nowrap px-3 py-3">
                          {adherent.nom} {adherent.prenom}
                        </td>

                        <td className="whitespace-nowrap px-3 py-3">
                          {formatDateFr(adherent.date_naissance)}
                        </td>

                        <td className="whitespace-nowrap px-3 py-3">
                          {beltName}
                        </td>

                        <td className="whitespace-nowrap px-3 py-3">
                          {lastDate}
                        </td>

                        <td className="whitespace-nowrap px-3 py-3">
                          <button
                            type="button"
                            onClick={() => openInfo(adherent.id)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700"
                            title="Chronologie & détails"
                            aria-label="Chronologie & détails"
                          >
                            <FaInfoCircle className="text-[14px]" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}

                  {filtered.length === 0 && !loading ? (
                    <tr>
                      <td
                        className="px-3 py-6 text-center text-slate-500 dark:text-slate-400"
                        colSpan={7}
                      >
                        Aucun résultat avec ces filtres.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            {loading ? (
              <div className="py-6">
                <Loader />
              </div>
            ) : null}
          </div>
        </div>

        {selectedIds.size > 0 ? (
          <div className="fixed bottom-4 left-1/2 z-40 -translate-x-1/2">
            <div className="flex flex-col items-center gap-2 rounded-2xl bg-white/95 px-4 py-3 shadow-2xl ring-1 ring-slate-200/70 dark:bg-slate-900/95 dark:ring-slate-700/70 sm:flex-row sm:gap-3">
              <span className="text-sm text-slate-700 dark:text-slate-200">
                {selectedIds.size} sélectionné
                {selectedIds.size > 1 ? "s" : ""}
              </span>

              <select
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                value={bulkBeltId}
                onChange={(event) => setBulkBeltId(event.target.value)}
                disabled={bulkSaving}
              >
                <option value="">Ceinture…</option>

                {sortedBelts.map((belt) => (
                  <option key={belt.id} value={belt.id}>
                    {belt.nom}
                  </option>
                ))}
              </select>

              <input
                type="date"
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                value={bulkDate}
                onChange={(event) => setBulkDate(event.target.value)}
                disabled={bulkSaving}
              />

              <button
                type="button"
                onClick={applyBulk}
                disabled={bulkSaving}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                title="Appliquer à la sélection"
              >
                {bulkSaving ? "Application..." : "Appliquer"}
              </button>

              <button
                type="button"
                onClick={clearBulkSelection}
                disabled={bulkSaving}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-slate-900 hover:bg-slate-200 disabled:opacity-60 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                title="Vider la sélection"
              >
                <FaTimes />
                <span className="hidden sm:inline">Annuler</span>
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {isModalOpen ? (
        <div className="fixed inset-0 z-[70]">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={closeInfo}
            aria-hidden
          />

          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="flex max-h-[85vh] w-full max-w-[860px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200/70 dark:bg-slate-900 dark:ring-slate-700/70">
              <div className="flex items-center justify-between rounded-t-2xl bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
                <h3 className="font-semibold text-white">
                  Chronologie des passages
                </h3>

                <button
                  type="button"
                  onClick={closeInfo}
                  className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-3 py-1.5 text-white hover:bg-white/20"
                >
                  <FaTimes />
                  <span>Fermer</span>
                </button>
              </div>

              <div className="overflow-y-auto p-5">
                <AdherentPassageCeinture
                  adherentId={selectedAdherent}
                  onBeltHistoryChange={handleHistoryChanged}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <ToastContainer />
    </section>
  );
};

export default PassageCeintureGrid;